import { LitElement, html, unsafeCSS, CSSResult } from 'lit';
import { property, query, queryAssignedElements } from 'lit/decorators.js';
import { ref, createRef, Ref } from 'lit/directives/ref.js';
import { literal } from 'lit/static-html.js';
import { C11nPrefix, C11nPrefixLiteral } from 'virtual:c11n-prefix';
import styles from './radio.scss?inline';
import slotStyles from './radio.slot.scss?inline';
import { appendStylesheet, c11nElement } from '../core';

@c11nElement()
export class RadioComponent extends LitElement {
  static selector = `${C11nPrefix}-radio`;
  static literal = literal`${C11nPrefixLiteral}-radio`;
  static override styles: CSSResult = unsafeCSS(styles);

  @property({ attribute: 'focus-label', type: Boolean, reflect: true })
  focusLabel: boolean = false;

  @property({ attribute: 'hide-label', type: Boolean, reflect: true})
  private hideLabel!: boolean;

  @property({ attribute: 'c11n-web-radio', type: Boolean, reflect: true })
  readonly _c11n_web_radio: boolean = true;

  @query('slot[name="input"]')
  inputSlotEl!: HTMLSlotElement;

  @queryAssignedElements({ slot: 'input', selector: 'input' })
  inputEls!: Array<HTMLInputElement>;

  @queryAssignedElements({ slot: 'label', selector: 'label' })
  labelEls!: Array<HTMLLabelElement>;

  get inputEl(): HTMLInputElement {
    return this.inputEls && this.inputEls[0];
  }

  get labelEl(): HTMLLabelElement {
    return this.labelEls && this.labelEls[0];
  }
  
  mutationObserver?: MutationObserver;
  radioButtonElRef: Ref<HTMLElement> = createRef<HTMLElement>();

  override connectedCallback(): void {
    super.connectedCallback();
    this.appendStyles();
    
    setTimeout(() => {
      this.handleLabelSlotChange();
    });
  }

  override updated(changedProperties: Map<any, any>): void {
    if (changedProperties.has('hideLabel')) {
      this.handlePositionRadioButton(this.hideLabel);
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.mutationObserver?.disconnect();
  }

  appendStyles() {
    const slotStylesheet: string = slotStyles.toString()
      .replaceAll(/\bc11n-web-radio\b/g, this.tagName.toLowerCase());
    appendStylesheet({ 
      target: this as Node, 
      styles: slotStylesheet, 
      name: `${this.tagName.toLowerCase()}-slot`
    });
  }

  handleLabelSlotChange(): void {
    if (this.radioButtonElRef.value) {
      this.setLabelMutationObserver(!!this.labelEl);
      
      this.hideLabel = !(!!this.labelEl);
    }
  }

  handlePositionRadioButton(hideLabel: boolean): void {
    if (hideLabel) {
      this.inputEl.insertAdjacentElement('afterend', this.radioButtonElRef.value!);
    } else {
      this.labelEl.insertAdjacentElement('afterbegin', this.radioButtonElRef.value!);
    }
  }

  setLabelMutationObserver(hasLabel: boolean): void {
    if (hasLabel) {
      this.mutationObserver = new MutationObserver(() => this.hideLabel = this.getIsLabelEmpty());
      this.mutationObserver.observe(this.labelEl, { childList: true, subtree: true }); 
    } else {
      this.mutationObserver?.disconnect();
      this.mutationObserver = undefined;
    }
  }

  getIsLabelEmpty(): boolean {
    return !(<string>this.labelEl?.textContent).trim().length;
  }

  override render() {
    return html`
      <slot name="input"></slot>
      <slot name="label" @slotchange="${this.handleLabelSlotChange}"></slot>
      ${this.hideLabel ? html` <slot></slot>` : html``}
      <span ${ref(this.radioButtonElRef)} class="c11n-radio-button__icon__container">
        <svg class="c11n-radio-button__icon" viewBox="0 0 16 16" aria-hidden="true" fill="transparent">
          <circle class="background" cx="8" cy="8" r="7.5"></circle>
          <circle class="foreground" cx="8" cy="8" r="4"></circle>
        </svg>  
      </span>
      `;
  }
}
export const C11nRadio = RadioComponent.selector;
export const C11nRadioLiteral = RadioComponent.literal;
